import { Filmomtale } from '@filmweb/sanity-types';
import classNames from "classnames";
import SMaxWidthMovieGrid, { SMaxWidthMovieGridHeader, SMovieCell } from 'components/moviegrid/SMaxWidthMovieGrid';
import MoviePoster from 'components/movieposter/MoviePoster';
import { useBaseTitle } from 'lib/client/useTitle';
import { setDefaultCacheHeaders } from 'lib/server/cache';
import { getCurrentMovies } from 'lib/server/movieinfo';
import { getFilmwebFilmomtale, getProgramLocalOmtaler, getServerDataWithSettings } from 'lib/server/sanity';
import { ICurrentMoviesProgram } from 'lib/types/Program';
import { DocumentType_CULTURE, ICommonProps, ICultureArticle, IMovieArticle, isDocumentType } from 'lib/types/sanity';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import styles from "./velgfilm.module.css";

const DynamicAdnuntiusAd = dynamic(() =>
	import('components/framework/AdnuntiusAd').then((mod) => mod.default),
	{ ssr: false }
);


//#region [Other]getServerSideProps
export const getServerSideProps: GetServerSideProps = getServerDataWithSettings(async (context, appData) => {
	setDefaultCacheHeaders(context as GetServerSidePropsContext);
	const currentMovies = await getCurrentMovies();

	const edis: string[] = currentMovies?.movies?.map(movie => movie.mainVersionId) ?? [];

	if ((edis?.length ?? 0) > 0) {
		if (appData.settings.canOverrideMovieTitles) {
			const localArts = await getProgramLocalOmtaler(edis, undefined);
			currentMovies.fwpakkeArticles = localArts?.reduce<Record<string, ICultureArticle | IMovieArticle>>((map, doc: (ICultureArticle | IMovieArticle)) => {
				let movieId: string = isDocumentType(doc, DocumentType_CULTURE) ? (doc as ICultureArticle).kulnr.current : (doc as IMovieArticle).edi.current;
				map[movieId] = doc;
				return map;
			}, {}) as Record<string, IMovieArticle>;
		}


		const fwMovieArticles = await getFilmwebFilmomtale(edis);
		currentMovies.filmwebMovies = fwMovieArticles?.reduce((previousValue: Record<string, Filmomtale>, currentValue) => {
			previousValue[currentValue.mainVersionEDI] = currentValue;
			return previousValue;
		}, {});
	}

	return {
		props: {
			currentMovies: currentMovies
		}, // will be passed to the page component as props

	}
});
//#endregion

//#region [Props]
type VelgFilmProps = {
	currentMovies: ICurrentMoviesProgram | undefined;
} & ICommonProps;
//#endregion

//#region [Component]
export default function VelgFilm({ currentMovies, appData }: VelgFilmProps) {
	const title = useBaseTitle(appData.settings, "Velg film");


	return <SMaxWidthMovieGrid className={classNames(styles.SVelgFilmMaxWidthMovieGrid)} as="section" $columns={2}>
		<Head>
			<title>{title}</title>
			<meta property="og:title" content={title} key="title" />
		</Head>
		<SMaxWidthMovieGridHeader $columns={2}>Velg film</SMaxWidthMovieGridHeader>
		{(currentMovies?.movies?.length ?? 0) === 0 && <div className={styles.SNoMovies}>For øyeblikket ingen filmer</div>}
		{(currentMovies?.movies?.length ?? 0) > 0 && <>
			<DynamicAdnuntiusAd className={styles.SAd} hideOnMobile={false} hideOnDesktop={true} appData={appData} index={0}/>
			{currentMovies!.movies.map(movie => <SMovieCell key={movie.mainVersionId}><MoviePoster edi={movie.mainVersionId} fallbackTitle={movie.title} fwpakkeFilmomtale={currentMovies!.fwpakkeArticles?.[movie.mainVersionId]} filmomtale={currentMovies!.filmwebMovies?.[movie.mainVersionId]} allowTitleOverride={appData.settings.canOverrideMovieTitles} /></SMovieCell>)}
		</>}
	</SMaxWidthMovieGrid>;
}
//#endregion